/**
 * What the delivery axis means to the surfaces that read it (RTE-006, RTE-017,
 * OPS-001).
 *
 * ## What this file used to be
 *
 * A translation table from `OrderStatus` - twenty-four values encoding five
 * different concerns - into the five phases the operations portal branches on.
 * It existed because three call sites had each written that ladder
 * independently and disagreed on screen: a delivered-then-refunded order read
 * "Created" beside its own arrival time and charged fee, and a row the admin
 * dashboard sorted top as LATE printed "Created" on its own delivery panel.
 *
 * Most of the file is gone, and this is why: the answers it computed were
 * answers about the DELIVERY, and the delivery is now its own lifecycle. There
 * is no ladder left to get wrong. `providerEtaMeaningful` is one comparison
 * against a state that says exactly what it means, rather than a lookup into a
 * table that also had to know about payment failures and cancelled orders.
 *
 * The `deliveredAt`-outranks-the-status rule is gone too, and that is the
 * clearest measure of the change. It existed because a delivered-then-refunded
 * order had its delivery fact overwritten by a financial one - the two shared
 * a column. They do not share anything now: such an order is COMPLETED on the
 * delivery axis and its refund is a case of its own, both true, neither ranked.
 */

import type { DeliveryJobStatus, DeliveryPhaseKind, OrderPhase } from '../enums';

/**
 * NO COURIER HAS BEEN ASKED FOR: the four states before a job exists.
 *
 * The one sentence behind every question a surface asks before dispatch, and
 * it is a function rather than a comparison because the family grew (plan 1.3).
 * `NOT_BOOKED` used to be the whole of it, so five call sites wrote
 * `delivery !== 'NOT_BOOKED'` and meant "a courier is engaged" - true right up
 * until the region started reporting the route estimate it takes at allocation,
 * which is EVERY order. On that day the guest order view would have opened the
 * live tracker on a morning three days away, the counter would have lost More
 * time on every pre-order, and an operator would have lost Reassign on all of
 * them - three different screens, one comparison, all correct when it was
 * written.
 *
 * The difference between the two is a platform concern and nothing else: a
 * route priced, or nothing yet. No guest and no counter is told them apart, and
 * nothing here should invent a way to.
 *
 * Exhaustive by construction: a new member of the union is a typecheck failure
 * here rather than a silent `false`, which would read as "a courier is out".
 */
export function deliveryUnbooked(delivery: DeliveryPhaseKind): boolean {
  switch (delivery) {
    case 'ESTIMATED':
      return true;
    case 'BOOKED':
    case 'COURIER_ASSIGNED':
    case 'AT_BAKER':
    case 'IN_TRANSIT':
    case 'ARRIVING':
    case 'COMPLETED':
    case 'NO_COURIER':
    case 'FAILED':
    // A bag riding back, or back: nobody is bringing anything to the guest.
    case 'RETURNING':
    case 'RETURNED':
      return false;
  }
}

/**
 * A COURIER IS OUT: a rider is engaged on this job and moving.
 *
 * The four states between a courier being assigned and the handover, and the
 * one question a surface asks when it wants to know whether anything about
 * this order is changing minute by minute rather than hour by hour - the live
 * map (GST-035) is what it is drawn for, and the guest page's poll cadence is
 * what reads it.
 *
 * `BOOKED` is deliberately NOT one of them, and that is the whole distinction:
 * a job exists and nobody has been assigned to it, so there is no position to
 * report and nothing to watch. `AT_BAKER` IS one - a rider standing at the
 * counter is a rider whose next move is the one the guest is waiting for
 * (BAK-044).
 *
 * Exhaustive by construction, on this directory's rule: a new member of the
 * union is a typecheck failure here rather than a silent `false`, which would
 * read as "nobody is riding" on the one screen that must never say that while
 * somebody is.
 */
export function deliveryCourierOut(delivery: DeliveryPhaseKind): boolean {
  switch (delivery) {
    case 'COURIER_ASSIGNED':
    case 'AT_BAKER':
    case 'IN_TRANSIT':
    case 'ARRIVING':
      return true;
    case 'ESTIMATED':
    case 'BOOKED':
    case 'COMPLETED':
    case 'NO_COURIER':
    case 'FAILED':
    // A bag riding back, or back: nobody is bringing anything to the guest.
    case 'RETURNING':
    case 'RETURNED':
      return false;
  }
}

/**
 * True when a provider ETA for this order is a fact rather than a leftover.
 *
 * A cached ETA from a job that was since cancelled or completed is not a fact
 * about this order any more, and serving it is what kept "ESTIMATED ARRIVAL
 * 07:28" on the row of an order with no courier.
 *
 * The three quoting states are false for the same reason under a different
 * name: a route estimate carries travel minutes, and travel minutes for a
 * route are not an arrival time for a rider nobody has hired.
 */
export function providerEtaMeaningful(delivery: DeliveryPhaseKind): boolean {
  switch (delivery) {
    case 'BOOKED':
    case 'COURIER_ASSIGNED':
    // A rider standing at the counter has spent the PICKUP estimate, but the
    // job is live and its dropoff estimate is what the guest is reading.
    case 'AT_BAKER':
    case 'IN_TRANSIT':
    case 'ARRIVING':
      return true;
    case 'ESTIMATED':
    case 'COMPLETED':
    case 'NO_COURIER':
    case 'FAILED':
    // A bag riding back, or back: nobody is bringing anything to the guest.
    case 'RETURNING':
    case 'RETURNED':
      return false;
  }
}

/**
 * True when the row needs an operator regardless of the clock.
 *
 * Its badge comes from this fact, never from ETA arithmetic against a job that
 * no longer exists - which is how a row once read "Late - 17 min spare".
 *
 * A quote that runs out is deliberately NOT one of them, and has no state of
 * its own any more (decision 13): the next dispatch asks again from the same
 * ESTIMATED with nobody's help, and putting it on the operator's board would be
 * an alert that clears itself - the kind an operator learns to ignore. What
 * DOES need somebody is a job that came to nothing (`NO_COURIER`) or a handover
 * that failed at the door (`FAILED`).
 *
 * A switch rather than two comparisons, so the next member of the union has to
 * answer this question out loud.
 */
export function deliveryNeedsOperator(delivery: DeliveryPhaseKind): boolean {
  switch (delivery) {
    case 'NO_COURIER':
    case 'FAILED':
    // The delivery failed and the bag is coming back, or is back (founder ruling #21).
    case 'RETURNING':
    case 'RETURNED':
      return true;
    case 'ESTIMATED':
    case 'BOOKED':
    case 'COURIER_ASSIGNED':
    case 'AT_BAKER':
    case 'IN_TRANSIT':
    case 'ARRIVING':
    case 'COMPLETED':
      return false;
  }
}

/**
 * DID THE RIDER TAKE THE BAG OFF THE COUNTER (BAK-004, BAK-026)?
 *
 * The counter's pickup countdown runs to the collection, so it must stop when
 * the collection happens - and since the September decisions the ORDER region
 * can no longer say on its own that it did. A handover that failed at the door
 * is `DELIVERY_FAILED`, which is legal only from `IN_TRANSIT` or `ARRIVING`
 * (the rider was carrying it) and which cancels the order in the same write,
 * so the order region reads CANCELLED and `orderBagCollected` answers false
 * over it. Read alone, that left a red overdue alarm running on a counter over
 * a bag that had left an hour earlier - which is exactly what a screen read at
 * arm's length cannot carry.
 *
 * `AT_BAKER` is the one that has to be argued: the rider is standing at the
 * counter, and the bag is still on it. The countdown is counting to precisely
 * that moment and must go on counting.
 *
 * A switch rather than a list, so the next member of the union answers out loud.
 */
export function deliveryBagLeftTheCounter(delivery: DeliveryPhaseKind): boolean {
  switch (delivery) {
    case 'IN_TRANSIT':
    case 'ARRIVING':
    case 'COMPLETED':
    // Reached the door and failed there: the goods left the shop (plan 1.3).
    case 'FAILED':
    // It left, and is riding back or is back: nothing to count down to.
    case 'RETURNING':
    case 'RETURNED':
      return true;
    case 'ESTIMATED':
    case 'BOOKED':
    case 'COURIER_ASSIGNED':
    case 'AT_BAKER':
    // Nobody is carrying it and nobody collected it: it is still the shop's.
    case 'NO_COURIER':
      return false;
  }
}

/** True when the delivery can no longer become late: it arrived, or it ended. */
export function deliveryRiskSettled(delivery: DeliveryPhaseKind): boolean {
  return delivery === 'COMPLETED' || deliveryNeedsOperator(delivery);
}

/**
 * DID THE BAG REACH THE GUEST (GST-037, GST-038)?
 *
 * The one question behind the tip, the two ratings, the receipt and the issue
 * form - and until now the one question the guest's own page had to answer for
 * itself. The server gates all of them on the delivery axis
 * (`handedOver` in `packages/domain`, which the browser cannot import and which
 * `packages/services` pins to this table member by member); `apps/guest-web`
 * restated it as a local `isDelivered`, so the control the guest sees and the
 * endpoint behind it were two sentences that happened to agree. This is the
 * name both sides ask now.
 *
 * A `Record` rather than a comparison, on this directory's own rule: the axis
 * gained `COURIER_ARRIVED` on 2026-08-26 (BAK-044), and a rider standing at the
 * counter is exactly the member a hand-written list gets wrong. An eleventh
 * member is a typecheck failure here rather than a silently `false` answer.
 *
 * Nothing on any other axis can contradict it. A delivered-then-refunded order
 * is COMPLETED here and its refund is a case of its own, neither ranked -
 * so the guest keeps the rating that explains the refund, which is the rating
 * worth having.
 */
const HANDED_OVER: Readonly<Record<DeliveryPhaseKind, boolean>> = {
  // Priced for the route. Nobody has been asked to carry anything.
  ESTIMATED: false,
  BOOKED: false,
  COURIER_ASSIGNED: false,
  // The rider is at the COUNTER. The guest is holding nothing.
  AT_BAKER: false,
  IN_TRANSIT: false,
  ARRIVING: false,
  COMPLETED: true,
  NO_COURIER: false,
  // Reached the door and the handover failed (PAY-013): nobody received it.
  FAILED: false,
  // The delivery failed and the bag went back (founder ruling #21).
  RETURNING: false,
  RETURNED: false,
};

export function deliveryHandedOver(delivery: DeliveryPhaseKind): boolean {
  return HANDED_OVER[delivery];
}

/**
 * THE SAME QUESTION ASKED OF AN ORDER, and the one the guest's controls ask.
 *
 * A courier the provider refused to stand down can deliver an order the
 * platform had already CANCELLED (#1471, R1): the delivery reads COMPLETED and
 * the guest was told the order was canceled. Nothing the guest can do on a
 * delivered order - tip, rate, report, fetch a receipt - is offered on one they
 * were told does not exist; what they may have received is the refund case's,
 * in Disputes. So an order is handed over when its bag reached the guest AND
 * the order was not cancelled. `handedOver` in `packages/domain` is the server's
 * half of this sentence, pinned to it by `packages/services/src/handed-over.test.ts`.
 */
export function orderHandedOver(order: {
  readonly order: OrderPhase;
  readonly delivery: DeliveryPhaseKind;
}): boolean {
  return order.order !== 'CANCELLED' && deliveryHandedOver(order.delivery);
}

/**
 * The provider-neutral job status the operations portal prints (RTE-017).
 *
 * One for one with the axis, because the axis IS the job's state. The old
 * version had to collapse a whole-order status into this and threw away the
 * last update the courier sent - it reported ARRIVING as "Picked up", on the
 * row where that matters most.
 */
export function deliveryJobStatusFor(delivery: DeliveryPhaseKind): DeliveryJobStatus {
  switch (delivery) {
    /*
     * An indicative price for a route is not a job, so operations reads the
     * same word it reads before anything happened. The neutral status keeps its
     * `QUOTED` member (RTE-017) and nothing produces it: the bookable quote is
     * taken and redeemed inside the booking write, so the region never rests
     * there (decision 13).
     */
    case 'ESTIMATED':
      return 'PENDING';
    case 'BOOKED':
      return 'CREATED';
    case 'COURIER_ASSIGNED':
    /*
     * Deliberately the same word, and NOT a new member of the neutral status.
     *
     * "The rider is at the counter" is a fact the COUNTER acts on; operations
     * acts on the job, and the job has not moved - nobody has collected
     * anything. A ninth `DeliveryJobStatus` would put a value on the
     * operations portal that no screen there reads and every provider adapter
     * would then have to answer for (RTE-017).
     */
    case 'AT_BAKER':
      return 'COURIER_ASSIGNED';
    case 'IN_TRANSIT':
      return 'PICKED_UP';
    case 'ARRIVING':
      return 'ARRIVING';
    case 'COMPLETED':
      return 'DELIVERED';
    case 'NO_COURIER':
      return 'CANCELLED';
    case 'FAILED':
      return 'FAILED';
    case 'RETURNING':
      return 'RETURNING';
    // Back at the counter: the job ended as a failure.
    case 'RETURNED':
      return 'FAILED';
  }
}

const MS_PER_MINUTE = 60_000;

function millisOf(instant: Date | string): number {
  return instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
}

/**
 * Whole minutes an arrival falls PAST the end of the promised window, floored
 * at zero (RTE-006, RTE-009).
 *
 * One arithmetic, two callers with two different arrivals. `assessLateness` in
 * the worker asks it about a PREDICTED arrival - the provider's ETA against a
 * live job - to decide whether to raise the RTE-006 alert. The bakery
 * performance history asks it about the ACTUAL arrival, on an order already
 * delivered, to decide whether that delivery was on time.
 *
 * They must agree about what "late" means, because a bakery scored as reliable
 * by one rule and alerted on by the other is the same two-answers-on-one-screen
 * defect this directory exists to close. The reference point is the window END
 * and only the end, and `Math.max(0, ...)` floors an arrival before it at zero
 * minutes past. Zero is not "on time": a PRE-ORDER handed over before its
 * window opens is EARLY, which is a missed promise (founder, 2026-10-07, "not
 * before, not after"), while an immediate order is never early. That verdict
 * is `deliveryTimingOutcomeOf`'s in `bakery-performance.ts`, and this function
 * only ever answers how late.
 *
 * The end both callers pass is the promise IN FORCE, `order.promisedWindow`:
 * for an immediate order the window its courier booking restated, the booked
 * pickup plus the quoted ride (RTE-044, founder 2026-10-07); for a pre-order
 * the slot the guest chose. Until 2026-10-07 an immediate order's was the
 * checkout estimate, so a slow answer from the counter read as a late ride.
 */
export function minutesPastPromisedWindow(
  arrival: Date | string,
  promisedWindowEnd: Date | string,
): number {
  const overshootMs = millisOf(arrival) - millisOf(promisedWindowEnd);
  if (!Number.isFinite(overshootMs)) return 0;
  return Math.max(0, Math.ceil(overshootMs / MS_PER_MINUTE));
}

/**
 * HAS THE COURIER JOB STOPPED - or was it never started (the domain's
 * `deliverySettled`, stated over the wire)?
 *
 * A `Record` rather than a comparison, on this directory's rule: a new member
 * of the axis is a typecheck failure here, not a silent answer. Pinned member
 * by member to the domain gate by `packages/services/src/state-sweep.test.ts`.
 */
const DELIVERY_SETTLED: Readonly<Record<DeliveryPhaseKind, boolean>> = {
  ESTIMATED: true,
  BOOKED: false,
  COURIER_ASSIGNED: false,
  AT_BAKER: false,
  IN_TRANSIT: false,
  ARRIVING: false,
  COMPLETED: true,
  NO_COURIER: true,
  FAILED: true,
  // Settled for the order at the return (founder ruling #21): the refund case
  // opens then; the bag arriving back is the one fact still admitted after.
  RETURNING: true,
  RETURNED: true,
};

/**
 * Has the courier job stopped, or was it never started? The table above,
 * asked by name - for a reader that must not speak while a rider may still be
 * carrying the bag, such as `guestPaidAndGotNothing` on a cancelled order whose
 * courier the provider refused to stand down (#1748).
 */
export function deliveryJobSettled(delivery: DeliveryPhaseKind): boolean {
  return DELIVERY_SETTLED[delivery];
}

/**
 * MAY AN OPERATOR CLOSE THIS ORDER'S DELIVERY (issue #1471, R4)?
 *
 * The order has ended and its courier job has not: the one position no
 * provider may ever leave, because the provider went silent, and the only one
 * Internal's Close delivery action is offered on. The domain's
 * `mayCloseDeliveryByOperations`, stated over the wire so the screen and the
 * machine ask one question; the state sweep pins the two to each other on every
 * reachable state.
 *
 * "Ended" is the domain's `orderEnded`, DELIVERED included - not
 * `order-phase.ts`'s narrower `orderStopped`, which answers whether the order has
 * STOPPED. A delivered order's job has always settled, so the two agree here in
 * practice; stating the wider one keeps this a copy of the gate rather than a
 * second rule.
 */
export function deliveryClosableByOperations(lifecycle: {
  readonly order: OrderPhase;
  readonly delivery: DeliveryPhaseKind;
}): boolean {
  if (DELIVERY_SETTLED[lifecycle.delivery]) return false;
  switch (lifecycle.order) {
    case 'DELIVERED':
    case 'CANCELLED':
    case 'CLOSED':
      return true;
    case 'CREATED':
    case 'SCHEDULED':
    case 'ACCEPTANCE_OPEN':
    case 'ACCEPTANCE_CALLING':
    case 'PREPARING':
    case 'READY':
    // Not ended: the engine stands the job down itself (#2155).
    case 'AWAITING_ASSIGNMENT':
    case 'COLLECTED':
      return false;
  }
}
